import { lazy, Suspense, useCallback, useEffect, useRef, useState, type MouseEvent } from 'react';
import {
  api,
  ApiError,
  auth0SettingsOf,
  distributorPath,
  readSelectedUserId,
  routePath,
  writeSelectedUserId,
  type DistributorWithRoutes,
  type Health,
  type Identity,
  type RouteDetails,
} from './api';
import { RouteIndex } from './RouteIndex';
import { GoogleRouteMap } from './GoogleRouteMap';
import { CustomersPage } from './CustomersPage';
import { RouteCustomers } from './RouteCustomers';
import { RequestsPage } from './RequestsPage';
import { SettingsPage } from './SettingsPage';
import type { HostedSession } from './HostedAuth';
import { appModeOf, hostedGateOf } from './session';
import { DemoAuth, DemoLoginForm } from './DemoAuth';

// The Auth0 SDK is loaded only by the hosted team demo; the local preview never requests it.
const HostedAuth = lazy(() =>
  import('./HostedAuth').then((module) => ({ default: module.HostedAuth })),
);

type Loadable<T> =
  | { state: 'idle' }
  | { state: 'loading' }
  | { state: 'ready'; data: T }
  | { state: 'error'; message: string; status?: number };

/** Error state for a failed request; keeps the HTTP status so the UI can offer the right recovery. */
function failed(error: unknown): { state: 'error'; message: string; status?: number } {
  return {
    state: 'error',
    message: describe(error),
    status: error instanceof ApiError ? error.status : undefined,
  };
}

/**
 * The URL is the source of truth for what is shown:
 *   /                                   all permitted distributors
 *   /customers                          customers grouped by route
 *   /requests                           request a new stop; the Xtreme queue
 *   /?distributors=1,2                 selected distributors in the route cards
 *   /distributors/:id                   one distributor's routes
 *   /distributors/:id/routes/:number    one route's stops
 * Routes are always addressed by distributor AND route number, so a shared
 * number such as 0415 never resolves to the wrong distributor.
 */
type View =
  | { kind: 'all'; distributorIds: number[] | null }
  | { kind: 'customers' }
  | { kind: 'requests' }
  | { kind: 'settings' }
  | { kind: 'distributor'; distributorId: number }
  | { kind: 'route'; distributorId: number; routeNumber: string }
  | { kind: 'invalid' };

const ROLE_LABEL: Record<Identity['role'], string> = {
  distributor: 'Distributor user',
  support_admin: 'Support admin',
  system_admin: 'System admin',
};

function parseView(pathname: string, search = ''): View {
  if (pathname === '/settings' || pathname === '/settings/') return { kind: 'settings' };
  if (pathname === '/customers' || pathname === '/customers/') return { kind: 'customers' };
  if (pathname === '/requests' || pathname === '/requests/') return { kind: 'requests' };
  if (pathname === '/') {
    const selected = new URLSearchParams(search).get('distributors');
    if (selected === null) return { kind: 'all', distributorIds: null };
    if (selected === '') return { kind: 'all', distributorIds: [] };
    const ids = selected.split(',');
    if (ids.some((id) => !/^[1-9]\d{0,8}$/.test(id))) return { kind: 'invalid' };
    return { kind: 'all', distributorIds: [...new Set(ids.map(Number))] };
  }
  const distributor = /^\/distributors\/([1-9]\d{0,8})\/?$/.exec(pathname);
  if (distributor) return { kind: 'distributor', distributorId: Number(distributor[1]) };
  const route = /^\/distributors\/([1-9]\d{0,8})\/routes\/([^/]+)\/?$/.exec(pathname);
  if (route) {
    try {
      return {
        kind: 'route',
        distributorId: Number(route[1]),
        routeNumber: decodeURIComponent(route[2]!),
      };
    } catch {
      return { kind: 'invalid' };
    }
  }
  return { kind: 'invalid' };
}

function sameView(a: View, b: View): boolean {
  if (a.kind !== b.kind) return false;
  if (a.kind === 'all' && b.kind === 'all') {
    return JSON.stringify(a.distributorIds) === JSON.stringify(b.distributorIds);
  }
  if (a.kind === 'distributor' && b.kind === 'distributor') {
    return a.distributorId === b.distributorId;
  }
  if (a.kind === 'route' && b.kind === 'route') {
    return a.distributorId === b.distributorId && a.routeNumber === b.routeNumber;
  }
  return true;
}

/**
 * History-backed view state: in-app links push a URL, back/forward pop it.
 * The view object is only replaced when the path really changes, so a
 * fragment jump (the skip link fires popstate too) does not re-run the
 * focus-and-scroll effect.
 */
function useView(): [View, (path: string) => void] {
  const [view, setView] = useState<View>(() =>
    parseView(window.location.pathname, window.location.search),
  );
  const apply = useCallback((path: string) => {
    const url = new URL(path, window.location.origin);
    const next = parseView(url.pathname, url.search);
    setView((current) => (sameView(current, next) ? current : next));
  }, []);
  useEffect(() => {
    const onPopState = () => apply(window.location.pathname + window.location.search);
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [apply]);
  const navigate = useCallback(
    (path: string) => {
      if (path !== window.location.pathname + window.location.search)
        window.history.pushState(null, '', path);
      apply(path);
    },
    [apply],
  );
  return [view, navigate];
}

const plural = (count: number, noun: string) => `${count} ${noun}${count === 1 ? '' : 's'}`;

function selectionPath(ids: number[], distributors: DistributorWithRoutes[]): string {
  if (ids.length === distributors.length) return '/';
  return `/?distributors=${[...ids].sort((a, b) => a - b).join(',')}`;
}

export function App() {
  const [view, navigate] = useView();
  const [health, setHealth] = useState<Loadable<Health>>({ state: 'loading' });
  const [identities, setIdentities] = useState<Loadable<Identity[]>>({ state: 'loading' });
  const [account, setAccount] = useState<Loadable<Identity>>({ state: 'idle' });
  const [selectedId, setSelectedId] = useState<number | null>(() => readSelectedUserId());
  // Hosted team demo only: the Auth0 session as HostedAuth reports it.
  const [hosted, setHosted] = useState<HostedSession | null>(null);
  const [distributors, setDistributors] = useState<Loadable<DistributorWithRoutes[]>>({
    state: 'idle',
  });
  const [route, setRoute] = useState<Loadable<RouteDetails>>({ state: 'idle' });
  const [refreshToken, setRefreshToken] = useState(0);
  const mainRef = useRef<HTMLElement>(null);
  const lastView = useRef(view);

  const routeKey = view.kind === 'route' ? `${view.distributorId}:${view.routeNumber}` : null;
  const modeReady = health.state === 'ready';
  const mode = modeReady ? appModeOf(health.data) : null;
  const previewMode = mode === 'preview';
  const hostedMode = mode === 'hosted';
  const registryMode =
    hostedMode &&
    modeReady &&
    health.data.dataSource === 'sql' &&
    health.data.previewDataset === 'imported';
  const auth0 = hostedMode && modeReady ? auth0SettingsOf(health.data) : null;
  const signedIn = hostedMode && hosted?.status === 'signed-in';
  const activeUserId = previewMode ? selectedId : null;
  const canLoad = modeReady && (previewMode ? selectedId !== null : account.state === 'ready');

  useEffect(() => {
    let cancelled = false;
    api
      .health()
      .then((data) => {
        if (cancelled) return;
        setHealth({ state: 'ready', data });
        if (data.devIdentity) {
          api
            .identities()
            .then(
              ({ identities }) => !cancelled && setIdentities({ state: 'ready', data: identities }),
            )
            .catch((error: unknown) => !cancelled && setIdentities(failed(error)));
        } else {
          setIdentities({ state: 'idle' });
        }
      })
      .catch((error: unknown) => !cancelled && setHealth(failed(error)));
    return () => {
      cancelled = true;
    };
  }, [refreshToken]);

  useEffect(() => {
    // The hosted demo asks for the account only once Auth0 reports a session.
    if (!modeReady || previewMode || mode === 'unavailable' || (hostedMode && !signedIn)) {
      setAccount({ state: 'idle' });
      return;
    }
    let cancelled = false;
    setAccount({ state: 'loading' });
    api
      .me(null)
      .then((data) => !cancelled && setAccount({ state: 'ready', data }))
      .catch((error: unknown) => !cancelled && setAccount(failed(error)));
    return () => {
      cancelled = true;
    };
  }, [modeReady, previewMode, hostedMode, signedIn, mode, refreshToken]);

  useEffect(() => {
    if (!canLoad) {
      setDistributors({ state: 'idle' });
      return;
    }
    let cancelled = false;
    setDistributors({ state: 'loading' });
    api
      .myDistributors(activeUserId)
      .then(
        ({ distributors }) => !cancelled && setDistributors({ state: 'ready', data: distributors }),
      )
      .catch((error: unknown) => !cancelled && setDistributors(failed(error)));
    return () => {
      cancelled = true;
    };
  }, [activeUserId, canLoad, refreshToken]);

  useEffect(() => {
    if (!canLoad || view.kind !== 'route') {
      setRoute({ state: 'idle' });
      return;
    }
    let cancelled = false;
    setRoute({ state: 'loading' });
    api
      .routeDetails(activeUserId, view.distributorId, view.routeNumber)
      .then(({ route }) => !cancelled && setRoute({ state: 'ready', data: route }))
      .catch((error: unknown) => !cancelled && setRoute(failed(error)));
    return () => {
      cancelled = true;
    };
    // routeKey captures the parts of `view` this request depends on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeUserId, canLoad, refreshToken, routeKey]);

  // A remembered identity that no longer exists is dropped once the list is known.
  useEffect(() => {
    if (!previewMode || identities.state !== 'ready' || selectedId === null) return;
    if (!identities.data.some((user) => user.id === selectedId)) {
      writeSelectedUserId(null);
      setSelectedId(null);
    }
  }, [identities, previewMode, selectedId]);

  const selected = previewMode
    ? identities.state === 'ready'
      ? identities.data.find((user) => user.id === selectedId)
      : undefined
    : account.state === 'ready'
      ? account.data
      : undefined;
  const accountName = selected?.displayName ?? 'Account';
  const adminView = selected?.role === 'support_admin' || selected?.role === 'system_admin';
  const gate = hostedMode ? hostedGateOf(hosted?.status, account) : null;
  const currentDistributor =
    (view.kind === 'distributor' || view.kind === 'route') && distributors.state === 'ready'
      ? distributors.data.find((item) => item.id === view.distributorId)
      : undefined;

  // After an in-app navigation, move focus to the content so keyboard and
  // screen-reader users land on the new page, and name the tab after it.
  useEffect(() => {
    // Compare with the previous view rather than a "first run" flag, so a
    // StrictMode double-invoked effect on mount does not steal focus.
    if (lastView.current === view) return;
    if (lastView.current.kind === 'all' && view.kind === 'all') {
      lastView.current = view;
      return;
    }
    lastView.current = view;
    mainRef.current?.focus({ preventScroll: true });
    window.scrollTo({ top: 0 });
  }, [view]);

  useEffect(() => {
    const parts: string[] = [];
    if (view.kind === 'route') parts.push(`Route ${view.routeNumber}`);
    if (currentDistributor) parts.push(currentDistributor.name);
    if (view.kind === 'all') parts.push('Routes');
    if (view.kind === 'customers') parts.push('Customers');
    if (view.kind === 'requests') parts.push('Requests');
    if (view.kind === 'settings') parts.push('Settings');
    if (view.kind === 'invalid') parts.push('Page not found');
    document.title = [...parts, 'Distributor Registry'].join(' · ');
  }, [view, currentDistributor]);

  function chooseIdentity(value: string) {
    const id = value === '' ? null : Number(value);
    writeSelectedUserId(id);
    setSelectedId(id);
    navigate('/');
  }

  /** In-app links navigate without a reload; everything else is left to the browser. */
  function onPageClick(event: MouseEvent<HTMLDivElement>) {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }
    const anchor = (event.target as Element).closest<HTMLAnchorElement>('a[href]');
    if (!anchor || anchor.target === '_blank' || anchor.hasAttribute('download')) return;
    const url = new URL(anchor.href, window.location.href);
    if (url.origin !== window.location.origin) return;
    if (url.hash && url.pathname === window.location.pathname) return;
    event.preventDefault();
    navigate(url.pathname + url.search);
  }

  return (
    <div className="page" onClick={onPageClick}>
      {auth0 && (
        <Suspense fallback={null}>
          <HostedAuth settings={auth0} onSession={setHosted} />
        </Suspense>
      )}
      {hostedMode && modeReady && health.data.auth?.mode === 'password' && (
        <DemoAuth onSession={setHosted} />
      )}
      <a className="skip-link" href="#main">
        Skip to content
      </a>
      <header className={`topbar${adminView ? ' topbar-admin' : ''}`}>
        <a className="brand" href="/" aria-label="XTREME Technologies Distributor Registry home">
          <img src="/xtreme-logo.png" alt="XTREME Technologies" />
        </a>
        <span className="product-name">Distributor Registry</span>
        <div className="header-account">
          {adminView && <span className="admin-view-label">Admin view</span>}
          {hostedMode && (
            <span className="demo-tag">{registryMode ? 'Registry' : 'Team demo'}</span>
          )}
          {previewMode && (
            <span className="demo-tag">
              {health.state === 'ready' && health.data.previewDataset === 'imported'
                ? 'Imported preview'
                : 'Sample data'}
            </span>
          )}
          {previewMode ? (
            <div className="identity-field">
              <label htmlFor="identity">Preview account</label>
              <select
                id="identity"
                value={selectedId ?? ''}
                onChange={(event) => chooseIdentity(event.target.value)}
                disabled={identities.state !== 'ready'}
              >
                <option value="">Choose a preview account</option>
                {identities.state === 'ready' &&
                  identities.data.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.displayName} · {ROLE_LABEL[user.role]}
                      {user.status === 'inactive' ? ' · inactive' : ''}
                    </option>
                  ))}
              </select>
            </div>
          ) : (
            <div className="account-summary" aria-label="Current account">
              <span className="account-avatar" aria-hidden="true">
                {selected?.displayName.charAt(0).toUpperCase() ?? '·'}
              </span>
              <span className="account-text">
                <strong>
                  {selected?.displayName ?? (signedIn ? hosted?.loginName : null) ?? 'Account'}
                </strong>
                <small>
                  {selected
                    ? ROLE_LABEL[selected.role] +
                      (signedIn && hosted?.loginName ? ` · ${hosted.loginName}` : '')
                    : signedIn
                      ? account.state === 'error'
                        ? registryMode
                          ? 'No Registry access'
                          : 'No demo access'
                        : 'Loading account…'
                      : 'Sign-in required'}
                </small>
              </span>
              {hostedMode && hosted && hosted.status !== 'loading' && (
                <button
                  type="button"
                  className="account-action"
                  onClick={signedIn ? hosted.signOut : hosted.signIn}
                >
                  {signedIn ? 'Sign out' : 'Sign in'}
                </button>
              )}
            </div>
          )}
        </div>
      </header>

      <div className="workspace">
        <nav className="product-nav" aria-label="Main navigation">
          <a
            href="/"
            aria-current={
              view.kind === 'customers' || view.kind === 'requests' || view.kind === 'settings'
                ? undefined
                : 'page'
            }
          >
            Routes
          </a>
          <a href="/customers" aria-current={view.kind === 'customers' ? 'page' : undefined}>
            Customers
          </a>
          <a href="/requests" aria-current={view.kind === 'requests' ? 'page' : undefined}>
            Requests
          </a>
          {selected?.role === 'support_admin' && selected.status === 'active' && (
            <a href="/settings" aria-current={view.kind === 'settings' ? 'page' : undefined}>
              Settings
            </a>
          )}
        </nav>
        <main className="content" id="main" ref={mainRef} tabIndex={-1}>
          <h1 className="visually-hidden">
            {view.kind === 'customers'
              ? 'Customers'
              : view.kind === 'requests'
                ? 'Requests'
                : view.kind === 'settings'
                  ? 'Settings'
                  : 'Routes'}
          </h1>

          {previewMode && identities.state === 'error' && (
            <div className="notice error" role="alert">
              Sample users could not load: {identities.message}
            </div>
          )}

          {health.state === 'error' ? (
            <div className="panel error" role="alert">
              App connection unavailable: {health.message}
            </div>
          ) : !modeReady ? (
            <div className="panel muted" role="status">
              Loading app…
            </div>
          ) : mode === 'unavailable' ? (
            <div className="panel error" role="alert">
              The team demo is not available: its server configuration is incomplete. Nothing can be
              shown until the demo owner finishes the setup.
            </div>
          ) : gate === 'checking' ? (
            <div className="panel muted" role="status">
              Checking sign-in…
            </div>
          ) : gate === 'not-authorized' ? (
            <NotAuthorizedPanel
              session={hosted}
              reason={account.state === 'error' ? account.message : ''}
              registry={registryMode}
            />
          ) : gate !== null ? (
            <SignInPanel gate={gate} session={hosted} registry={registryMode} />
          ) : !previewMode && account.state === 'error' ? (
            <div className="panel error" role="alert">
              {account.status === 401
                ? 'Please sign in to view your routes.'
                : `Your account could not load: ${account.message}`}
            </div>
          ) : !previewMode && account.state !== 'ready' ? (
            <div className="panel muted" role="status">
              Loading account…
            </div>
          ) : view.kind === 'invalid' ? (
            <div className="panel error">
              This page was not found. <a href="/">View routes</a>.
            </div>
          ) : previewMode && selectedId === null ? (
            <div className="panel welcome">
              <span className="welcome-icon" aria-hidden="true">
                ↗
              </span>
              <h2>Choose a preview account</h2>
              <p>
                Choose an account above to review its routes. Distributor accounts see their
                assigned routes; the all-distributors and support accounts show every route.
              </p>
            </div>
          ) : (
            <section aria-busy={distributors.state === 'loading' || route.state === 'loading'}>
              {distributors.state === 'loading' && (
                <div className="panel muted" role="status">
                  Loading distributors…
                </div>
              )}
              {distributors.state === 'error' && (
                <div className="panel error" role="alert">
                  {distributors.message}{' '}
                  {distributors.status === 403 ? (
                    previewMode && (
                      <span className="muted">Choose another sample user to continue.</span>
                    )
                  ) : (
                    <button type="button" onClick={() => setRefreshToken((n) => n + 1)}>
                      Try again
                    </button>
                  )}
                </div>
              )}
              {distributors.state === 'ready' &&
                distributors.data.length === 0 &&
                view.kind !== 'customers' &&
                view.kind !== 'requests' &&
                view.kind !== 'settings' && (
                  <div className="panel empty">
                    No distributors or routes are assigned to this user.
                  </div>
                )}

              {distributors.state === 'ready' &&
                distributors.data.length > 0 &&
                view.kind === 'all' && (
                  <RouteIndex
                    distributors={distributors.data}
                    selectedDistributorIds={view.distributorIds}
                    onSelectDistributors={(ids) => navigate(selectionPath(ids, distributors.data))}
                  />
                )}

              {distributors.state === 'ready' && view.kind === 'customers' && (
                <CustomersPage
                  distributors={distributors.data}
                  userId={activeUserId}
                  accountName={accountName}
                />
              )}

              {distributors.state === 'ready' && view.kind === 'requests' && (
                <RequestsPage
                  distributors={distributors.data}
                  userId={activeUserId}
                  role={selected?.role ?? 'distributor'}
                />
              )}

              {view.kind === 'settings' && selected && (
                <SettingsPage userId={activeUserId} role={selected.role} />
              )}

              {distributors.state === 'ready' &&
                (view.kind === 'distributor' || view.kind === 'route') &&
                !currentDistributor && (
                  <div className="panel error" role="alert">
                    This distributor is not available to the selected user.{' '}
                    <a href="/">View routes</a>.
                  </div>
                )}

              {distributors.state === 'ready' &&
                currentDistributor &&
                view.kind === 'distributor' && (
                  <>
                    <Breadcrumb
                      trail={[{ label: 'All routes', href: '/' }]}
                      current={currentDistributor.name}
                    />
                    <RouteIndex
                      distributors={distributors.data}
                      selectedDistributorIds={[currentDistributor.id]}
                      onSelectDistributors={(ids) =>
                        navigate(selectionPath(ids, distributors.data))
                      }
                    />
                  </>
                )}

              {currentDistributor && view.kind === 'route' && (
                <>
                  <Breadcrumb
                    trail={[
                      { label: 'All routes', href: '/' },
                      {
                        label: currentDistributor.name,
                        href: distributorPath(currentDistributor.id),
                      },
                    ]}
                    current={`Route ${view.routeNumber}`}
                  />
                  <div className="section-heading">
                    <div>
                      <p className="eyebrow">{currentDistributor.name}</p>
                      <h2>Route {view.routeNumber}</h2>
                    </div>
                    {route.state === 'ready' && (
                      <span className="count-pill">
                        {plural(route.data.stops.length, 'customer')}
                      </span>
                    )}
                  </div>
                  {route.state === 'loading' && (
                    <div className="panel muted" role="status">
                      Loading customers…
                    </div>
                  )}
                  {route.state === 'error' && (
                    <div className="panel error" role="alert">
                      {route.message}{' '}
                      {route.status === 404 ? (
                        <a href={distributorPath(currentDistributor.id)}>
                          Back to {currentDistributor.name}
                        </a>
                      ) : (
                        <button type="button" onClick={() => setRefreshToken((n) => n + 1)}>
                          Try again
                        </button>
                      )}
                    </div>
                  )}
                  {route.state === 'ready' && route.data.stops.length === 0 && (
                    <div className="panel empty">This route has no customers yet.</div>
                  )}
                  {route.state === 'ready' && route.data.stops.length > 0 && (
                    <>
                      <GoogleRouteMap
                        stops={route.data.stops}
                        userId={activeUserId}
                        label={`Route ${route.data.routeNumber} for ${route.data.distributorName}`}
                      />
                      <RouteCustomers
                        key={`${route.data.distributorId}:${route.data.routeNumber}`}
                        route={route.data}
                        accountName={accountName}
                      />
                    </>
                  )}
                </>
              )}
            </section>
          )}
        </main>
      </div>

      <footer className="status">
        <span>
          {previewMode
            ? health.state === 'ready' && health.data.previewDataset === 'imported'
              ? 'Local preview · imported customer records'
              : 'Team preview · fictional records'
            : registryMode
              ? 'Imported customer records'
              : hostedMode
                ? health.state === 'ready' && health.data.previewDataset === 'imported'
                  ? 'Team demo · imported customer records'
                  : 'Team demo · fictional records'
                : 'XTREME Technologies'}
        </span>
        {health.state === 'ready' && (
          <span>
            {health.data.status === 'ok' ? 'App ready' : 'Data unavailable'} ·{' '}
            {health.data.dataSource === 'memory'
              ? health.data.previewDataset === 'imported'
                ? 'Imported preview data'
                : 'Sample data'
              : registryMode
                ? 'Registry data'
                : hostedMode
                  ? 'Demo database'
                  : 'SQL data'}
          </span>
        )}
        {health.state === 'error' && (
          <span className="error">API unreachable: {health.message}</span>
        )}
      </footer>
    </div>
  );
}

function Breadcrumb({
  trail,
  current,
}: {
  trail: { label: string; href: string }[];
  current: string;
}) {
  return (
    <nav className="breadcrumb" aria-label="Breadcrumb">
      <ol>
        {trail.map((item) => (
          <li key={item.href}>
            <a href={item.href}>{item.label}</a>
            <span aria-hidden="true"> /</span>
          </li>
        ))}
        <li aria-current="page">{current}</li>
      </ol>
    </nav>
  );
}

/** The hosted demo's signed-out states: not signed in yet, a failed sign-in, or an ended session. */
function SignInPanel({
  gate,
  session,
  registry = false,
}: {
  gate: 'signed-out' | 'sign-in-failed' | 'session-ended';
  session: HostedSession | null;
  registry?: boolean;
}) {
  return (
    <div className="panel welcome" role={gate === 'signed-out' ? undefined : 'alert'}>
      <span className="welcome-icon" aria-hidden="true">
        ↗
      </span>
      <h2>
        {gate === 'session-ended'
          ? 'Your session has ended'
          : gate === 'sign-in-failed'
            ? 'Sign-in did not complete'
            : registry
              ? 'Sign in to the Distributor Registry'
              : 'Sign in to the team demo'}
      </h2>
      <p>
        {gate === 'session-ended'
          ? 'Sign in again to continue.'
          : gate === 'sign-in-failed'
            ? `The sign-in service reported: ${session?.message ?? 'an unknown error'}. Try again, or ask the demo owner for access.`
            : registry
              ? session?.authenticate
                ? 'Sign in with your Registry username and password.'
                : 'Sign in with your Registry account.'
              : 'Sign in with the demo account provided to you to access the Distributor Registry.'}
      </p>
      {session?.authenticate ? (
        <DemoLoginForm session={session} />
      ) : (
        <button type="button" onClick={session?.signIn}>
          {gate === 'signed-out' ? 'Sign in' : 'Sign in again'}
        </button>
      )}
    </div>
  );
}

/** A verified sign-in that the server's access list does not include: nothing is shown. */
function NotAuthorizedPanel({
  session,
  reason,
  registry = false,
}: {
  session: HostedSession | null;
  reason: string;
  registry?: boolean;
}) {
  return (
    <div className="panel welcome" role="alert">
      <span className="welcome-icon" aria-hidden="true">
        !
      </span>
      <h2>
        {registry ? 'This sign-in has no Registry access' : 'This sign-in has no demo access'}
      </h2>
      <p>
        You are signed in{session?.loginName ? ` as ${session.loginName}` : ''}, but no registry
        information is available to this sign-in. {reason}
      </p>
      {session?.subject && (
        <p>
          Sign-in ID to send to the demo owner: <code>{session.subject}</code>
        </p>
      )}
      <button type="button" onClick={session?.signOut}>
        Sign out
      </button>
    </div>
  );
}

function describe(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 401) {
      return error.message;
    }
    if (error.status === 403) return error.message;
    if (error.status === 404) return 'This route was not found for this distributor.';
    return error.message;
  }
  return error instanceof Error ? error.message : 'Unknown error.';
}
